<template>
    <div class="relative">
        <span v-if="isModal" class="text-purple-200 font-Montserrat font-bold uppercase mb-10">
            {{ $t('contact.send_a_message') }}
        </span>

        <div
            :class="[
                !isThankYouMessageVisible ? 'opacity-1 pointer-events-all' : 'opacity-0 pointer-events-none',
                'transition-opacity',
            ]"
        >
            <ValidationObserver ref="form" slim>
                <form novalidate class="col-span-6" @submit.prevent="onSubmit">
                    <div class="grid grid-cols-6 gap-5">
                        <div v-if="$i18n.locale !== 'hu'" class="col-span-6 sm:col-span-3">
                            <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                <InputText
                                    v-model="first_name"
                                    name="first_name"
                                    :placeholder="$t('contact.first_name')"
                                    :class="{ 'p-invalid': errors.length }"
                                    type="text"
                                />
                                <InputError :errors="errors" class="ml-5" />
                            </ValidationProvider>
                        </div>

                        <div class="col-span-6 sm:col-span-3">
                            <ValidationProvider v-slot="{ errors }" vid="last_name" tag="div">
                                <InputText
                                    v-model="last_name"
                                    name="last_name"
                                    :placeholder="$t('contact.last_name')"
                                    :class="{ 'p-invalid': errors.length }"
                                    type="text"
                                />
                                <InputError :errors="errors" class="ml-5" />
                            </ValidationProvider>
                        </div>

                        <div v-if="$i18n.locale === 'hu'" class="col-span-6 sm:col-span-3">
                            <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                <InputText
                                    v-model="first_name"
                                    name="first_name"
                                    :placeholder="$t('contact.first_name')"
                                    :class="{ 'p-invalid': errors.length }"
                                    type="text"
                                />
                                <InputError :errors="errors" class="ml-5" />
                            </ValidationProvider>
                        </div>

                        <div class="col-span-6">
                            <ValidationProvider v-slot="{ errors }" vid="email" tag="div">
                                <InputText
                                    v-model="email"
                                    name="email"
                                    :placeholder="$t('contact.email_address')"
                                    :class="{ 'p-invalid': errors.length }"
                                    type="email"
                                />
                                <InputError :errors="errors" class="ml-5" />
                            </ValidationProvider>
                        </div>

                        <div class="col-span-6 mb-4">
                            <ValidationProvider v-slot="{ errors }" vid="message" tag="div">
                                <Textarea
                                    v-model="message"
                                    name="message"
                                    rows="5"
                                    :class="{ 'p-invalid': errors.length }"
                                    :placeholder="$t('contact.message')"
                                />
                                <InputError :errors="errors" class="ml-5" />
                            </ValidationProvider>
                        </div>

                        <div class="col-span-6 -mt-8">
                            <ValidationProvider v-slot="{ errors }" vid="files" tag="div">
                                <FileUpload
                                    ref="applicationAttachments"
                                    :multiple="true"
                                    accept="image/*,application/pdf,application/msword"
                                    :max-file-size="5000000"
                                    :choose-label="$t('jobs.application_form.file_upload.label')"
                                    :file-limit="5"
                                    :invalid-file-size-message="
                                        $t('jobs.application_form.file_upload.errors.file_size')
                                    "
                                    :invalid-file-type-message="
                                        $t('jobs.application_form.file_upload.errors.file_type')
                                    "
                                    :invalid-file-limit-message="
                                        $t('jobs.application_form.file_upload.errors.file_limit')
                                    "
                                >
                                </FileUpload>

                                <InputError :errors="errors" class="mt-2" />
                            </ValidationProvider>
                        </div>

                        <div class="col-span-6 sm:col-span-4" :class="isModal && 'text-black'">
                            <ValidationProvider v-slot="{ errors }" vid="privacy" tag="div">
                                <div class="p-field flex items-start">
                                    <Checkbox
                                        id="newsletter_privacy_policy_cbx"
                                        v-model="accept_privacy_policy"
                                        :binary="true"
                                    />

                                    <label for="newsletter_privacy_policy_cbx">
                                        {{ $t('contact.accept_policy.msg1') }}
                                        <a
                                            :href="localePath({ name: 'privacy-policy' })"
                                            target="_blank"
                                            class="font-bold desktop:hover:underline"
                                        >
                                            {{ $t('contact.accept_policy.msg2') }}
                                        </a>
                                    </label>
                                </div>

                                <InputError :errors="errors" />
                            </ValidationProvider>

                            <div class="mt-5">
                                <RecaptchaDisclaimer />
                            </div>
                        </div>

                        <NuxtLink
                            v-if="$store.state.jobs.lastSearchQuery"
                            :to="getBackButtonPath()"
                            class="p-component p-button p-button-secondary p-button-text mt-8 sm:hidden col-span-3"
                        >
                            <span class="pi pi-arrow-left p-button-icon p-button-icon-left"></span>
                            <span class="p-button-label">{{ $t('generic.back') }}</span>
                        </NuxtLink>

                        <div class="col-span-3 sm:col-span-2 sm:col-start-5 mt-8 sm:mt-0 text-right">
                            <Button
                                type="submit"
                                :label="$t('jobs.apply')"
                                class="p-button-primary w-full text-center flex justify-center"
                            />
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </div>
        <div
            class="transition-opacity absolute top-0 left-0 w-full h-full bg-white flex flex-col justify-center"
            :class="[
                isThankYouMessageVisible ? 'opacity-1 pointer-events-all' : 'opacity-0 pointer-events-none',
                isModal && 'text-black',
            ]"
        >
            <p class="font-Abhaya-Libre font-bold text-2xl mb-5">{{ $t('contact.message_sent_feedback.msg1') }}</p>

            <p class="text-15 mb-12">
                {{ $t('contact.message_sent_feedback.msg2') }}
            </p>

            <Button :label="$t('generic.back')" class="w-fit p-button-primary" @click="closeThankYouScreen" />
        </div>
    </div>
</template>

<script>
import FileUpload from 'primevue/fileupload';
import RecaptchaDisclaimer from '@/components/UI/RecaptchaDisclaimer';

export default {
    name: 'JobApplicationForm',
    components: {
        FileUpload,
        RecaptchaDisclaimer,
    },
    props: {
        isModal: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data() {
        return {
            first_name: '',
            last_name: '',
            email: '',
            message: '',
            accept_privacy_policy: false,
            isThankYouMessageVisible: false,
            files: [],
        };
    },
    async mounted() {
        await this.$recaptcha.init();
    },
    methods: {
        async onSubmit() {
            try {
                const token = await this.$recaptcha.execute('contact');

                const formData = new FormData();

                formData.append('first_name', this.first_name);
                formData.append('last_name', this.last_name);
                formData.append('message', this.message);
                formData.append('email', this.email);
                formData.append('privacy', this.accept_privacy_policy ? '1' : '0');
                formData.append('type', 2);

                this.$refs.applicationAttachments.files.forEach((file, i) => {
                    formData.append('files[]', this.$refs.applicationAttachments.files[i]);
                });

                await this.$axios
                    .$post('/recaptcha/validate/multipart/contact', formData, {
                        baseURL: '',
                        headers: {
                            'content-type': 'multipart/form-data',
                            'base-url': this.$axios.defaults.baseURL,
                            'recaptcha-token': token,
                        },
                    })
                    .then((res) => {
                        if (res.success === 1) {
                            this.isThankYouMessageVisible = true;
                        } else {
                            this.$refs.form.setErrors(res.error);
                        }
                    });
            } catch (err) {
                console.log(err.message);
            }

            await this.$recaptcha.reset();
        },
        closeThankYouScreen() {
            this.first_name = '';
            this.last_name = '';
            this.email = '';
            this.company_name = '';
            this.position = '';
            this.message = '';
            this.accept_privacy_policy = false;
            this.$refs.applicationAttachments.clear();
            this.isThankYouMessageVisible = false;

            this.$refs.form.reset();
        },
        getBackButtonPath() {
            return this.localePath(
                `/job-seeker/jobs?${new URLSearchParams(this.$store.state.jobs.lastSearchQuery).toString()}`
            );
        },
    },
};
</script>
